import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiBook, FiCode, FiTerminal, FiDatabase, FiShield, FiSearch, FiChevronRight, FiCpu } = FiIcons;

const Docs = () => {
  const sections = [
    {
      title: "Getting Started",
      icon: FiBook,
      links: ["Installation", "Your First Workflow", "The .hd Extension", "Project Structure"]
    },
    {
      title: "The .hd DSL",
      icon: FiCode,
      links: ["Syntax Overview", "Clinical Decorators", "Model Orchestration", "Environment Variables"]
    },
    {
      title: "Models & Engines",
      icon: FiCpu,
      links: ["Venice-Clinical v4", "Hugging Face Setup", "Local LLM Execution", "Fine-tuning Patterns"]
    },
    {
      title: "Compliance",
      icon: FiShield,
      links: ["HIPAA Configuration", "Audit Log API", "Patient Vault Keys", "FDA Regulatory Paths"]
    }
  ];

  return (
    <div className="min-h-screen bg-brand-ivory pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-4 gap-12">
          {/* Sidebar */}
          <aside className="hidden lg:block space-y-10">
            <div className="relative">
              <input 
                type="text" 
                placeholder="Search docs..." 
                className="w-full pl-10 pr-4 py-3 bg-white border border-gray-100 rounded-xl text-xs focus:ring-2 focus:ring-brand-mint transition-all shadow-sm"
              />
              <SafeIcon icon={FiSearch} className="absolute left-3.5 top-3 text-gray-400" />
            </div>
            {sections.map(section => (
              <div key={section.title}>
                <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center space-x-2">
                  <SafeIcon icon={section.icon} />
                  <span>{section.title}</span>
                </h3>
                <ul className="space-y-3">
                  {section.links.map(link => (
                    <li key={link}>
                      <a href="#" className="text-sm font-medium text-brand-slate hover:text-brand-indigo transition-colors flex items-center justify-between group">
                        <span>{link}</span>
                        <SafeIcon icon={FiChevronRight} className="text-[10px] opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 transition-all" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-[3rem] p-12 lg:p-20 border border-gray-100 shadow-sm"
            >
              <div className="prose prose-indigo max-w-none">
                <h1 className="text-5xl font-display font-bold text-brand-indigo mb-6">Documentation Portal</h1>
                <p className="text-xl text-brand-slate leading-relaxed mb-12">
                  Welcome to the HDAI technical documentation. This portal covers everything from basic workflow design to advanced clinical model orchestration using the .hd DSL.
                </p>

                <div className="grid md:grid-cols-2 gap-8 mb-16">
                  <div className="p-8 bg-brand-ivory rounded-3xl border border-gray-100">
                    <h3 className="text-xl font-bold text-brand-indigo mb-4 flex items-center space-x-2">
                      <SafeIcon icon={FiTerminal} className="text-brand-mint" />
                      <span>The HDAI CLI</span>
                    </h3>
                    <p className="text-sm text-brand-slate mb-6">Automate your clinical deployments with our powerful command-line interface.</p>
                    <code className="block bg-brand-indigo text-brand-mint p-4 rounded-xl text-xs font-mono">npm i -g @hdai/cli</code>
                  </div>
                  <div className="p-8 bg-brand-ivory rounded-3xl border border-gray-100">
                    <h3 className="text-xl font-bold text-brand-indigo mb-4 flex items-center space-x-2">
                      <SafeIcon icon={FiCode} className="text-brand-mint" />
                      <span>.hd Language Spec</span>
                    </h3>
                    <p className="text-sm text-brand-slate mb-6">Learn the primitives of our Domain-Specific Language for healthcare.</p>
                    <button className="text-brand-indigo font-bold text-sm underline hover:text-brand-mint">View Specification</button>
                  </div>
                </div>

                <h2 className="text-3xl font-display font-bold text-brand-indigo mb-6">The .hd Philosophy</h2>
                <p className="text-brand-slate leading-relaxed mb-8">
                  HDAI Software treats clinical logic as **verifiable code**. Unlike traditional black-box AI, the .hd DSL allows clinicians to define hard constraints and "Clinician-in-the-Loop" checkpoints directly in the workflow logic.
                </p>

                <div className="bg-brand-indigo rounded-2xl p-8 text-white font-mono text-sm mb-12">
                  <div className="text-brand-mint mb-2">@jurisdiction("us")</div>
                  <div className="text-brand-mint mb-2">@workflow("cardiology_triage")</div>
                  <div className="text-white/60">node Triage {`{`}</div>
                  <div className="pl-4">engine = venice.clinical_v4</div>
                  <div className="pl-4">prompt = "Assess chest pain severity..."</div>
                  <div className="pl-4 text-brand-mint">require("clinician_approval") {`//`} mandatory gate</div>
                  <div className="text-white/60">{`}`}</div>
                </div>

                <div className="flex items-center justify-between p-8 border-t border-gray-100 mt-20">
                  <div className="text-brand-slate text-sm">Was this page helpful?</div>
                  <div className="flex space-x-4">
                    <button className="px-6 py-2 bg-brand-ivory rounded-xl text-sm font-bold hover:bg-brand-mint transition-all">Yes</button>
                    <button className="px-6 py-2 bg-brand-ivory rounded-xl text-sm font-bold hover:bg-red-50 transition-all">No</button>
                  </div>
                </div>
              </div>
            </motion.div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Docs;